import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-97874de0.js';
import { a as anime } from './anime.es-a5520566.js';
import { c as closestIxMenu } from './context-42311cff.js';
import { c as createMutationObserver } from './mutation-observer-db8757e6.js';
import './typed-event-ad6484c5.js';

/*
 * SPDX-FileCopyrightText: 2024 Siemens AG
 *
 * SPDX-License-Identifier: MIT
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
function createEnterLeaveDebounce(enterCallback, leaveCallback, { debounceTimeEnter, debounceTimeLeave, } = {
    debounceTimeEnter: 0,
    debounceTimeLeave: 500,
}) {
    let enterTimeout;
    let leaveTimeout;
    return {
        onEnter: () => {
            clearTimeout(leaveTimeout);
            enterTimeout = setTimeout(() => {
                enterCallback();
            }, debounceTimeEnter);
        },
        onLeave: () => {
            clearTimeout(enterTimeout);
            leaveTimeout = setTimeout(() => {
                leaveCallback();
            }, debounceTimeLeave);
        },
    };
}

const menuCategoryCss = ":host{display:flex;flex-direction:column;position:relative}:host *,:host *::after,:host *::before{box-sizing:border-box}:host ::-webkit-scrollbar-button{display:none}@-moz-document url-prefix(){:host *{scrollbar-color:var(--theme-scrollbar-thumb--background) var(--theme-scrollbar-track--background);scrollbar-width:thin}}:host ::-webkit-scrollbar{width:0.5rem;height:0.5rem}:host ::-webkit-scrollbar-track{border-radius:5px;background:var(--theme-scrollbar-track--background)}:host ::-webkit-scrollbar-track:hover{background:var(--theme-scrollbar-track--background--hover)}:host ::-webkit-scrollbar-thumb{border-radius:5px;background:var(--theme-scrollbar-thumb--background)}:host ::-webkit-scrollbar-thumb:hover{background:var(--theme-scrollbar-thumb--background--hover)}:host ::-webkit-scrollbar-corner{display:none}:host .category{display:flex;position:relative;align-items:center;width:100%;height:100%}:host .category-text{width:100%;padding-right:0.25rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}:host .category-chevron{margin-left:auto;margin-right:0;transition:var(--theme-default-time) transform ease-in-out}:host .category-chevron--open{transform:rotate(-180deg)}:host .menu-items{overflow:hidden;max-height:0;transition:var(--theme-default-time) max-height ease-in-out}:host .menu-items--expanded{max-height:999999999px;padding:0.25rem 0 0.25rem 1.625rem}:host .menu-items--collapsed{display:none}:host .category-dropdown ::slotted(ix-menu-item){--ix-menu-item-height:2.5rem}:host .category-dropdown-header{pointer-events:none;padding-left:0.125rem;min-width:256px}:host ::slotted(ix-menu-item){--ix-menu-item-height:2.5rem}:host(.expanded){background-color:var(--theme-color-ghost--active)}:host ::slotted(a[href]){text-decoration:none !important}";
const IxMenuCategoryStyle0 = menuCategoryCss;

const DefaultIxMenuItemHeight = 40;
const DefaultAnimationTimeout = 150;
const MenuCategory = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.closeOtherCategories = createEvent(this, "closeOtherCategories", 7);
        this.enterLeaveDebounce = createEnterLeaveDebounce(() => {
            this.onPointerEnter();
        }, () => {
            this.onPointerLeave();
        });
        this.label = undefined;
        this.icon = undefined;
        this.notifications = undefined;
        this.menuExpand = false;
        this.showItems = false;
        this.showDropdown = false;
        this.nestedItems = [];
    }
    isNestedItemActive() {
        return this.getNestedItems().some((item) => item.active);
    }
    getNestedItems() {
        return Array.from(this.hostElement.querySelectorAll(':scope ix-menu-item'));
    }
    getNestedItemsHeight() {
        const items = this.getNestedItems();
        return items.length * DefaultIxMenuItemHeight;
    }
    onExpandCategory(showItems) {
        if (showItems) {
            this.animateFadeIn();
        }
        else {
            this.animateFadeOut();
        }
    }
    animateFadeOut() {
        const slotHideThresholdMs = 25;
        anime({
            targets: this.menuItemsContainer,
            duration: DefaultAnimationTimeout,
            easing: 'easeInSine',
            opacity: [1, 0],
            maxHeight: [this.getNestedItemsHeight() + DefaultIxMenuItemHeight, 0],
            complete: () => {
                setTimeout(() => {
                    this.showItems = false;
                    this.showDropdown = false;
                }, DefaultAnimationTimeout + slotHideThresholdMs);
            },
        });
    }
    animateFadeIn() {
        anime({
            targets: this.menuItemsContainer,
            duration: DefaultAnimationTimeout,
            easing: 'easeInSine',
            opacity: [0, 1],
            maxHeight: [0, this.getNestedItemsHeight() + DefaultIxMenuItemHeight],
            begin: () => {
                this.showItems = true;
                this.showDropdown = false;
            },
        });
    }
    onPointerEnter() {
        var _a;
        if ((_a = this.ixMenu) === null || _a === void 0 ? void 0 : _a.expand) {
            return;
        }
        this.closeOtherCategories.emit();
        this.showDropdown = true;
    }
    onPointerLeave() {
        this.showDropdown = false;
    }
    onCategoryClick(e) {
        var _a;
        e.stopPropagation();
        if ((_a = this.ixMenu) === null || _a === void 0 ? void 0 : _a.expand) {
            this.onExpandCategory(!this.showItems);
            return;
        }
    }
    onNestedItemsChanged(mutations) {
        this.nestedItems = this.getNestedItems();
        if (!this.menuExpand || this.showItems || !mutations) {
            return;
        }
        for (const mutation of mutations !== null && mutations !== void 0 ? mutations : []) {
            if (mutation.attributeName === 'class' &&
                mutation.target instanceof HTMLElement &&
                mutation.target.classList.contains('active')) {
                this.showItems = true;
                this.onExpandCategory(true);
                return;
            }
        }
    }
    isCategoryItemListVisible() {
        return this.menuExpand && (this.showItems || this.isNestedItemActive());
    }
    componentWillLoad() {
        const closestMenu = closestIxMenu(this.hostElement);
        if (!closestMenu) {
            throw Error('ix-menu-category can only be used as a child of ix-menu');
        }
        this.ixMenu = closestMenu;
        this.menuExpand = this.ixMenu.expand;
        this.showItems = this.isCategoryItemListVisible();
    }
    componentDidLoad() {
        var _a;
        this.observer = createMutationObserver((mutations) => this.onNestedItemsChanged(mutations));
        this.observer.observe(this.hostElement, {
            attributes: true,
            childList: true,
            subtree: true,
        });
        requestAnimationFrame(() => {
            this.onNestedItemsChanged();
        });
        (_a = this.ixMenu) === null || _a === void 0 ? void 0 : _a.addEventListener('expandChange', ({ detail: menuExpand }) => {
            this.menuExpand = menuExpand;
            if (!menuExpand) {
                this.clearMenuItemStyles();
            }
            this.showItems = this.isCategoryItemListVisible();
        });
    }
    clearMenuItemStyles() {
        var _a, _b;
        (_a = this.menuItemsContainer) === null || _a === void 0 ? void 0 : _a.style.removeProperty('max-height');
        (_b = this.menuItemsContainer) === null || _b === void 0 ? void 0 : _b.style.removeProperty('opacity');
    }
    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
    render() {
        return (h(Host, { key: 'e71d240316eb6b94bb380ef38095a980c414e3da', class: {
                expanded: this.showItems,
            }, onPointerEnter: () => {
                this.enterLeaveDebounce.onEnter();
            }, onPointerLeave: (event) => {
                if (event.pointerType === 'touch') {
                    return;
                }
                this.enterLeaveDebounce.onLeave();
            } }, h("ix-menu-item", { key: 'a944c473517dc6bdfbe17557de92ea23e33a636b', class: 'category-parent', active: this.isNestedItemActive(), notifications: this.notifications, icon: this.icon, onClick: (e) => this.onCategoryClick(e), onFocus: () => this.onPointerEnter(), isCategory: true }, h("div", { key: '92d971d7fb4b9e93c17e5f6dce66a4c71d15df40', class: "category" }, h("div", { key: '60f7d2d47cd58a9f52d5061d8758ce7e293265d7', class: "category-text" }, this.label), h("ix-icon", { key: '5e5acccf3735496059acd2c96b604d2d2a911ab9', name: 'chevron-down-small', class: {
                'category-chevron': true,
                'category-chevron--open': this.showItems,
            } }))), h("div", { key: '6cd4983793650c000968615488bf5432ebff8ae5', ref: (ref) => (this.menuItemsContainer = ref), class: {
                'menu-items': true,
                'menu-items--expanded': this.showItems,
                'menu-items--collapsed': !this.showItems,
            } }, this.showItems ? h("slot", null) : null), h("ix-dropdown", { key: '865f01f86e242c671c98bbb0c69a34fd13d57191', closeBehavior: 'both', show: this.showDropdown, onShowChanged: ({ detail: dropdownShown }) => {
                this.showDropdown = dropdownShown;
            }, class: 'category-dropdown', anchor: this.hostElement, placement: "right-start", offset: {
                mainAxis: 3,
            }, onClick: (e) => {
                if (e.target instanceof HTMLElement) {
                    if (e.target.tagName === 'IX-MENU-ITEM') {
                        this.showDropdown = false;
                    }
                    else {
                        e.preventDefault();
                    }
                }
            } }, h("ix-dropdown-item", { key: '479772ea8b9b93dc9891b9055a49fcfdd8c29b24', class: 'category-dropdown-header' }, h("ix-typography", { key: '700e4fb48634c9c6ab5a626dc3fabdc9212b6aa3', format: "label", bold: true, color: "std" }, this.label)), h("ix-divider", { key: 'ffc1422f9d4db6f6b1d32767935678d55ef8231a' }), h("slot", { key: 'a56cd8de6fb1829795ac0866bd2d4d31b672b24a' }))));
    }
    get hostElement() { return getElement(this); }
};
MenuCategory.style = IxMenuCategoryStyle0;

export { MenuCategory as ix_menu_category };

//# sourceMappingURL=ix-menu-category.entry.js.map